import {
  Permission,
  useAddABACPolicyToPermissionMutation,
  useCreatePermissionMutation,
  useDeletePermissionMutation,
  useRemoveABACPolicyFromPermissionMutation,
  useUpdatePermissionMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UsePermissionMutationProps = {}

export type UsePermissionMutationResult = {
  createPermission: (permission: Partial<Permission>, onSuccess: ((permission: Permission) => void) | undefined) => void
  updatePermission: (permission: Partial<Permission>, onSuccess: (() => void) | undefined) => void
  deletePermission: (permissionId: string, onSuccess: (() => void) | undefined) => void
  addABACPolicy: (permissionId: string, abacPolicyId: string, onSuccess: (() => void) | undefined) => void
  removeABACPolicy: (permissionId: string, abacPolicyId: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
  isAddingABACPolicy?: boolean
  isRemovingABACPolicy?: boolean
}

export const usePermissionMutation = (props: UsePermissionMutationProps = {}): UsePermissionMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)
  const [_addingABACPolicy, _setAddingABACPolicy] = useDebouncedState(false, 500)
  const [_removingABACPolicy, _setRemovingABACPolicy] = useDebouncedState(false, 500)

  // possible mutations
  const [_createPermission, {isLoading: isCreating}] = useCreatePermissionMutation()
  const [_updatePermission, {isLoading: isUpdating}] = useUpdatePermissionMutation()
  const [_deletePermission, {isLoading: isDeleting}] = useDeletePermissionMutation()
  const [_addABACPolicy, {isLoading: isAddingABACPolicy}] = useAddABACPolicyToPermissionMutation()
  const [_removeABACPolicy, {isLoading: isRemovingABACPolicy}] = useRemoveABACPolicyFromPermissionMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    _setAddingABACPolicy(isAddingABACPolicy)
  }, [isAddingABACPolicy, _setAddingABACPolicy])

  useEffect(() => {
    _setRemovingABACPolicy(isRemovingABACPolicy)
  }, [isRemovingABACPolicy, _setRemovingABACPolicy])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "usePermissionUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.permission", "Permission"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePermissionUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating || _addingABACPolicy || _removingABACPolicy) {
      notifications.show({
        id: "usePermissionUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.permission", "Permission"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePermissionUpdate_updating")
    }
  }, [_updating, _addingABACPolicy, _removingABACPolicy, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "usePermissionUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.permission", "Permission"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePermissionUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<Permission>, onSuccess: ((permission: Permission) => void) | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.permission", "Permission") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess(payload)
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.permission", "Permission") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.permission", "Permission") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((permissionId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deletePermission({id: permissionId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deletePermission, handleDeleteResult])

  const createPermission = useCallback((permission: Partial<Permission>, onSuccess: ((permission: Permission) => void) | undefined) => {
    const promise = _createPermission(permission).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createPermission, handleCreateResult])

  const updatePermission = useCallback((permission: Partial<Permission>, onSuccess: (() => void) | undefined) => {
    const promise = _updatePermission(permission).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updatePermission, handleUpdateResult])

  const deletePermission = useCallback((permissionId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.permission", "Permission"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.permission.confirm", "Are you sure you want to delete this permission?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(permissionId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  const addABACPolicy = useCallback((permissionId: string, abacPolicyId: string, onSuccess: (() => void) | undefined) => {
    const promise = _addABACPolicy({permissionId, abacPolicyId}).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_addABACPolicy, handleUpdateResult])

  const handleRemoveABACPolicyConfirm = useCallback((permissionId: string, abacPolicyId: string, onSuccess: (() => void) | undefined) => {
    const promise = _removeABACPolicy({permissionId, abacPolicyId}).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_removeABACPolicy, handleUpdateResult])

  const removeABACPolicy = useCallback((permissionId: string, abacPolicyId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.remove", "Remove") + " " + t("common.entity.abacpolicy", "ABAC Policy"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.remove.permissionpolicy.confirm", "Are you sure you want to remove this ABAC Policy from this permission?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.remove", "Remove"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleRemoveABACPolicyConfirm(permissionId, abacPolicyId, onSuccess)
      },
    })
  }, [handleRemoveABACPolicyConfirm, t])

  return {
    createPermission: createPermission,
    updatePermission: updatePermission,
    deletePermission: deletePermission,
    addABACPolicy: addABACPolicy,
    removeABACPolicy: removeABACPolicy,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
    isAddingABACPolicy: _addingABACPolicy,
    isRemovingABACPolicy: _removingABACPolicy,
  }
}
